// src/api/useApi.js
import { useState, useCallback } from "react";
import { auth } from "../services/firebaseServices";
import { API_BASE } from "../apiBase";

// Wrap rows so call sites can use record.get('n') (Neo4j-style) OR record.n directly.
// Node-shaped values from the server are { identity, labels, properties }.
function withGetters(json) {
  const rows = (json && json.records) || [];
  return {
    records: rows.map((row) => ({
      get: (key) => row[key],
      ...row,
    })),
  };
}

// Low-level call. Returns { records: [...] } where each record has a .get(key) method,
// matching the old use-neo4j result shape so every component works unchanged.
export async function callApi(endpoint, params = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("not signed in");
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}/${endpoint}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(params),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || res.statusText);
  return withGetters(json);
}

// Drop-in replacement for useLazyReadCypher / useLazyWriteCypher:
//   const [run, { result, loading, error }] = useListLetters();
//   await run({ year, offset, search });
export function makeApiHook(endpoint) {
  return function useEndpoint() {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const run = useCallback(async (params = {}) => {
      setLoading(true);
      setError(null);
      try {
        const shaped = await callApi(endpoint, params); // already wrapped with .get()
        setResult(shaped);
        return shaped;
      } catch (e) {
        setError(e);
        throw e;
      } finally {
        setLoading(false);
      }
    }, []);

    return [run, { result, loading, error }];
  };
}

// ---- Session / identity ----
export const useGetMe = makeApiHook("getMe");

// ---- ELetter ----
export const useListLetters = makeApiHook("listLetters");
export const useListLetterIds = makeApiHook("listLetterIds");
export const useCountLetters = makeApiHook("countLetters");
export const useGetLetter = makeApiHook("getLetter");
export const useCreateLetter = makeApiHook("createLetter");
export const useUpdateLetter = makeApiHook("updateLetter");
export const useSetTagsString = makeApiHook("setTagsString");
export const useDeleteLetter = makeApiHook("deleteLetter");
export const useExportLetters = makeApiHook("exportLetters");

// ---- EFile ----
export const useListLetterFiles = makeApiHook("listLetterFiles");
export const useCreateFile = makeApiHook("createFile");
export const useUpdateFile = makeApiHook("updateFile");
export const useDeleteFile = makeApiHook("deleteFile");

// ---- Per-letter tagging ----
export const useGetLetterTags = makeApiHook("getLetterTags");
export const useLinkTag = makeApiHook("linkTag");
export const useSetTagLevel = makeApiHook("setTagLevel");
export const useUnlinkTag = makeApiHook("unlinkTag");

// ---- Tag tree ----
export const useListRootFolders = makeApiHook("listRootFolders");
export const useListTrendingFolders = makeApiHook("listTrendingFolders");
export const useListFolderChildren = makeApiHook("listFolderChildren");
export const useSearchTagItems = makeApiHook("searchTagItems");
export const useCreateRootFolder = makeApiHook("createRootFolder");
export const useCreateChildFolder = makeApiHook("createChildFolder");
export const useCreateTag = makeApiHook("createTag");
export const useUpdateItem = makeApiHook("updateItem");
export const useReorderChild = makeApiHook("reorderChild");
export const useReorderRoot = makeApiHook("reorderRoot");
export const useLinkChild = makeApiHook("linkChild");
export const useUnlinkChild = makeApiHook("unlinkChild");
export const useDeleteItem = makeApiHook("deleteItem");
export const useLinkLetterToFolder = makeApiHook("linkLetterToFolder");

// ---- EArticle ----
export const useListArticles = makeApiHook("listArticles");
export const useGetArticle = makeApiHook("getArticle");
export const useSearchArticles = makeApiHook("searchArticles");
export const useCreateArticle = makeApiHook("createArticle");
export const useUpdateArticle = makeApiHook("updateArticle");
export const useDeleteArticle = makeApiHook("deleteArticle");
export const useNotify = makeApiHook("notify");

// ---- DailyInsights ----
export const useListInsights = makeApiHook("listInsights");
export const useCreateInsight = makeApiHook("createInsight");
export const useUpdateInsight = makeApiHook("updateInsight");
export const useRelinkInsight = makeApiHook("relinkInsight");

// ---- ETrending ----
export const useListTrending = makeApiHook("listTrending");
export const useCreateTrending = makeApiHook("createTrending");
export const useUpdateTrending = makeApiHook("updateTrending");
export const useReorderTrending = makeApiHook("reorderTrending");
export const useDeleteTrending = makeApiHook("deleteTrending");

// ---- EUser ----
export const useListUsers = makeApiHook("listUsers");
export const useGetUser = makeApiHook("getUser");
export const useCreateUser = makeApiHook("createUser");
export const useUpdateUser = makeApiHook("updateUser");
export const useSetUserSettings = makeApiHook("setUserSettings");
export const useSetUserEnabled = makeApiHook("setUserEnabled");
export const useSetUserRole = makeApiHook("setUserRole");
export const useDeleteUser = makeApiHook("deleteUser");

// ---- Hebrew Letter DB ----
export const useListHebrew = makeApiHook("listHebrew");
export const useGetHebrew = makeApiHook("getHebrew");
export const useCreateHebrew = makeApiHook("createHebrew");
export const useUpdateHebrew = makeApiHook("updateHebrew");
export const useDeleteHebrew = makeApiHook("deleteHebrew");
